/**
 * Shared caseload helpers for display and formatting.
 */
import { formatUsDateShort } from '@/lib/dateHelpers';
import type { Role } from '@/lib/rbacRoles';
import type { Client, ClientEpisode, ClientGroup } from '../types';
import { mockClientGroups } from './caseloadEmptyState';

/** One row in the Caseload table: one Episode of Care with its client reference. */
export interface EpisodeOfCareRow {
  episode: ClientEpisode;
  /** Primary client for this row (first member for merged rows). */
  client: Client;
  /** All clients in this row (populated for merged couples/family rows). */
  clients?: Client[];
  /** Override display name for merged rows (e.g. "Alex Rivera & Jordan Rivera"). */
  displayName?: string;
}

/**
 * Distinct EpisodeOfCare care managers (episode.provider) for supervisee clients — used for
 * provider filter options. Client-level `supervisee` alone is wrong when one patient has multiple
 * EoCs with different care managers.
 */
export function uniqueSuperviseeCareManagersFromClients(clients: Client[]): string[] {
  const set = new Set<string>();
  for (const c of clients) {
    if (c.relationshipType !== 'Supervisee') continue;
    if (c.episodes?.length) {
      for (const ep of c.episodes) {
        if (ep.provider) set.add(ep.provider);
      }
    } else if (c.supervisee) {
      set.add(c.supervisee);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export interface ProviderFilterOption {
  /** Practitioner id when known, else the display name (legacy rows without a care-manager id). */
  value: string;
  label: string;
}

/** Fixed leading options for the caseload provider filter; named providers follow a divider. */
export const PROVIDER_FILTER_MY_CLIENTS = 'my-clients';
export const PROVIDER_FILTER_MY_TEAM = 'supervisee-caseload';

/**
 * Named provider options for the caseload provider filter, derived from the episode rows the
 * viewer can already see (which are role-scoped upstream: ALL → every provider, PRACTICE → same
 * org, TEAM → supervisees, OWN → self). The viewer is excluded because "My Clients" covers them.
 */
export function providerFilterOptionsFromRows(
  rows: EpisodeOfCareRow[],
  viewerPractitionerId?: string,
): ProviderFilterOption[] {
  const byValue = new Map<string, string>();
  for (const { episode } of rows) {
    if (!episode.provider) continue;
    const id = episode.careManagerPractitionerId;
    if (viewerPractitionerId && id === viewerPractitionerId) continue;
    byValue.set(id ?? episode.provider, episode.provider);
  }
  return [...byValue.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export interface EpisodeRowProviderFilterOptions {
  /** Logged-in user's Practitioner id — required for My Clients / My Team semantics. */
  viewerPractitionerId?: string;
}

/**
 * Provider filter for episode-based caseload rows. Matches each row on **this episode’s**
 * care manager (`episode.careManagerPractitionerId` / `episode.provider`), not client-level
 * `relationshipType` alone (which breaks for ALL-scope viewers like Unify Admin).
 */
export function episodeRowMatchesProviderFilter(
  row: EpisodeOfCareRow,
  providerFilter: string[],
  opts?: EpisodeRowProviderFilterOptions,
): boolean {
  if (providerFilter.length === 0) return false;
  const members = row.clients ?? [row.client];
  const epProvider = row.episode.provider;
  const viewerId = opts?.viewerPractitionerId;
  const episodeCareManagerId = row.episode.careManagerPractitionerId;

  const matchesMyClients = (): boolean => {
    if (viewerId && episodeCareManagerId) {
      return episodeCareManagerId === viewerId;
    }
    // No practitioner identity (or no care manager on the episode): fall back to the
    // client-level relationship so My Clients + My Team still covers every row.
    return members.some((c) => c.relationshipType !== 'Supervisee');
  };

  const matchesSuperviseeCaseload = (): boolean => {
    if (viewerId && episodeCareManagerId) {
      return episodeCareManagerId !== viewerId;
    }
    return members.some((c) => c.relationshipType === 'Supervisee');
  };

  if (providerFilter.includes(PROVIDER_FILTER_MY_CLIENTS) && matchesMyClients()) {
    return true;
  }
  if (providerFilter.includes(PROVIDER_FILTER_MY_TEAM) && matchesSuperviseeCaseload()) {
    return true;
  }
  for (const f of providerFilter) {
    if (f === PROVIDER_FILTER_MY_CLIENTS || f === PROVIDER_FILTER_MY_TEAM) continue;
    // Named option values are practitioner ids when known, display names otherwise.
    if (f === episodeCareManagerId || f === epProvider) return true;
  }
  return false;
}

/** Admin roles that oversee a whole practice (or the platform) rather than a personal caseload. */
const CASELOAD_ADMIN_ROLES: readonly Role[] = [
  'unify-admin',
  'practice-owner',
  'practice-admin',
];

/**
 * Starting provider filter. Admins oversee everyone, so they open on My Clients + My Team
 * (which together cover every row they can see); clinicians open on their own caseload.
 */
export function defaultProviderFilterForRole(role: Role | undefined): string[] {
  return role && CASELOAD_ADMIN_ROLES.includes(role)
    ? [PROVIDER_FILTER_MY_CLIENTS, PROVIDER_FILTER_MY_TEAM]
    : [PROVIDER_FILTER_MY_CLIENTS];
}

/**
 * Value for a multi-select that defaults to "everything". `null` means the user has not
 * touched it, so late-loading options stay included; `[]` is an explicit "show nothing".
 */
export function resolveAllByDefaultFilter(
  selection: string[] | null,
  options: ReadonlyArray<{ value: string }>,
): string[] {
  return selection ?? options.map((option) => option.value);
}

/** For client-level tabs (e.g. inactive list): match named provider to any episode care manager. */
export function clientMatchesNamedCareManager(client: Client, name: string): boolean {
  if (client.episodes?.some((e) => e.provider === name)) return true;
  return client.supervisee === name;
}

const STATUS_COLORS: Record<string, string> = {
  Active: 'green',
  Inactive: 'red',
  Discharged: 'gray',
  Unresponsive: 'orange',
  'Pending Transfer': 'yellow',
  'Pending Consultation': 'blue',
  'Follow up': 'violet',
};

const EPISODE_STATUS_COLORS: Record<string, string> = {
  planned: 'gray',
  waitlist: 'blue',
  active: 'green',
  'follow-up': 'violet',
  'on-hold': 'orange',
  finished: 'gray',
};

/** Human-readable label for caseload episode status badges/filters. */
export function formatEpisodeStatusLabel(status: string): string {
  switch (status) {
    case 'follow-up':
      return 'Follow up';
    case 'on-hold':
      return 'On Hold';
    default:
      return status;
  }
}

/** Preferred / display name (preferred first + last, or client.name). */
export function getClientDisplayName(client: Client): string {
  const preferred = [client.preferredFirstName, client.preferredLastName].filter(Boolean).join(' ').trim();
  return preferred || client.name;
}

/** Legal name (first + last) for display in "Preferred (Legal)" format. */
export function getClientLegalName(client: Client): string {
  return [client.firstName, client.lastName].filter(Boolean).join(' ').trim();
}

export function getStatusColor(status: string): string {
  return STATUS_COLORS[status] || 'gray';
}

export function getEpisodeStatusColor(status: string): string {
  return EPISODE_STATUS_COLORS[status] || 'gray';
}

const MERGED_GROUP_TYPES = new Set<string>(['Couples Therapy', 'Family Therapy']);

/** Episode names that correspond to group types we merge. */
const MERGED_EPISODE_NAMES = new Set<string>(['Couples Therapy', 'Family Therapy']);

/**
 * Find the ClientGroup for a client's couples/family episode, if one exists.
 */
function findMergeGroup(
  client: Client,
  episodeName: string,
  groups: ClientGroup[],
): ClientGroup | undefined {
  if (!MERGED_EPISODE_NAMES.has(episodeName) || !client.groups?.length) return undefined;
  const keyword = episodeName.split(' ')[0].toLowerCase(); // "couples" or "family"
  return client.groups
    .map((gId) => groups.find((g) => g.id === gId))
    .find((g) => g && MERGED_GROUP_TYPES.has(g.type) && g.type.toLowerCase().includes(keyword));
}

function lastNameFromDisplayName(display: string): string {
  const parts = display.trim().split(/\s+/);
  return parts.length ? (parts[parts.length - 1] ?? '') : '';
}

/**
 * Build a display name for a merged group row.
 * - Couples: "Alex Rivera & Jordan Rivera" (uses ` & ` between member display names)
 * - Family: "Rivera Family" (shared last name) or "Rivera & Smith Family" (mixed)
 *
 * Uses `ClientGroup.members[].clientName` when there is no `Client` (e.g. RelatedPerson-only members).
 */
function buildGroupDisplayName(group: ClientGroup, clientById: Map<string, Client>): string {
  const resolveDisplay = (m: ClientGroup['members'][number]) => {
    const c = clientById.get(m.clientId);
    return c ? getClientDisplayName(c) : m.clientName;
  };
  if (group.type === 'Couples Therapy') {
    return group.members.map(resolveDisplay).join(' & ');
  }
  const lastNames = [
    ...new Set(
      group.members
        .map((m) => {
          const c = clientById.get(m.clientId);
          if (c?.lastName) return c.lastName;
          return lastNameFromDisplayName(m.clientName);
        })
        .filter(Boolean),
    ),
  ];
  const familySurname = lastNames.length <= 2 ? lastNames.join(' & ') : lastNames[0];
  return `${familySurname} Family`;
}

/** Minimal `Client` for group members who are not caseload rows (e.g. RelatedPerson-only). */
function placeholderClientForGroupMember(m: ClientGroup['members'][number], template: Client): Client {
  const parts = m.clientName.trim().split(/\s+/);
  const firstName = parts[0] ?? m.clientName;
  const lastName = parts.slice(1).join(' ').trim() || template.lastName || '';
  return {
    id: m.clientId,
    name: m.clientName,
    firstName,
    lastName,
    preferredFirstName: '',
    preferredLastName: '',
    pronouns: '',
    clientType: template.clientType,
    status: template.status,
    relationshipType: 'Coverage',
    firstSession: template.firstSession,
    mostRecentAppointment: template.mostRecentAppointment,
    nextAppointment: template.nextAppointment ?? '',
    outstandingItems: template.outstandingItems ?? [],
    provider: template.provider,
  };
}

/**
 * Maps clients into Episode-of-Care–based rows. Each client's episodes produce one row each;
 * if a client has no episodes array, one row is generated from the client's core fields.
 *
 * Couples and Family therapy episodes that share a ClientGroup are merged into a single row.
 * @param groups – group definitions used for merging; defaults to mockClientGroups.
 */
export function getEpisodeRows(clients: Client[], groups?: ClientGroup[]): EpisodeOfCareRow[] {
  const effectiveGroups = groups ?? mockClientGroups;
  const rows: EpisodeOfCareRow[] = [];
  const clientById = new Map(clients.map((c) => [c.id, c]));
  const mergedGroupIds = new Set<string>();

  for (const client of clients) {
    const episodes = client.episodes?.length
      ? client.episodes
      : [
          {
            id: `e-${client.id}`,
            name: client.serviceType === 'IOP' ? 'Intensive Outpatient' : client.serviceType === 'Med Management' ? 'Medication Management' : 'Individual Therapy',
            status: mapClientStatusToEpisodeStatus(client.status),
            startDate: client.firstSession,
            endDate: client.status === 'Discharged' || client.status === 'Follow up' ? client.mostRecentAppointment : undefined,
            provider: client.provider,
          } as ClientEpisode,
        ];
    for (const episode of episodes) {
      const group = findMergeGroup(client, episode.name, effectiveGroups);
      if (group) {
        if (mergedGroupIds.has(group.id)) continue;
        mergedGroupIds.add(group.id);
        const ipMember = group.members[0];
        const primaryClient =
          (ipMember ? clientById.get(ipMember.clientId) : undefined) ?? client;
        const mergedClients = group.members.map((m) => {
          const c = clientById.get(m.clientId);
          return c ?? placeholderClientForGroupMember(m, primaryClient);
        });
        rows.push({
          episode,
          client: mergedClients[0] ?? client,
          clients: mergedClients,
          displayName: buildGroupDisplayName(group, clientById),
        });
      } else {
        rows.push({ episode, client });
      }
    }
  }
  return rows;
}

function mapClientStatusToEpisodeStatus(
  status: Client['status']
): ClientEpisode['status'] {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Follow up':
      return 'follow-up';
    case 'Discharged':
      return 'finished';
    case 'Inactive':
    case 'Unresponsive':
      return 'finished';
    case 'Pending Transfer':
    case 'Pending Consultation':
      return 'active';
    default:
      return 'active';
  }
}

const EMPTY_DATE_TOKENS = new Set(['', '—', '--']);

function isPresentDate(value: string | null | undefined): value is string {
  return Boolean(value && !EMPTY_DATE_TOKENS.has(value.trim()));
}

/**
 * First session for a caseload row: appointment-derived date, then episode `period.start`.
 */
export function resolveFirstSessionDate(
  appointmentDerived: string | null | undefined,
  episodePeriodStart: string | null | undefined,
): string {
  if (isPresentDate(appointmentDerived)) return appointmentDerived.trim();
  if (isPresentDate(episodePeriodStart)) return episodePeriodStart.trim();
  return '';
}

/** Caseload table date cell: `MMM D, YYYY` for calendar dates, em dash when missing. */
export function formatCaseloadDate(dateString: string | null | undefined): string {
  if (!isPresentDate(dateString)) return '—';
  return formatUsDateShort(dateString) || '—';
}

export function getDaysSinceLastAppointment(dateString: string): number | null {
  if (!dateString) return null;
  try {
    const diff = new Date().getTime() - new Date(dateString).getTime();
    return Number.isNaN(diff) ? null : Math.floor(diff / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

export const CLIENT_TYPE_OPTIONS = [
  { value: 'all', label: 'All Client Types' },
  { value: 'Adult', label: 'Adult' },
  { value: 'Minor', label: 'Minor' },
] as const;

export const ROWS_PER_PAGE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
] as const;

/** Whether the Clients tab should include patients without an EpisodeOfCare (staff directory view). */
export function shouldMergeDirectoryOnlyCaseloadClients(
  viewScope: 'OWN' | 'TEAM' | 'PRACTICE' | 'ALL' | 'CUSTOM',
): boolean {
  return viewScope === 'PRACTICE' || viewScope === 'ALL' || viewScope === 'CUSTOM';
}

/** Merge episode-scoped caseload rows with directory-only clients (no EpisodeOfCare). */
export function mergeCaseloadDirectoryClients(
  episodeClients: Client[],
  directoryOnlyClients: Client[],
): Client[] {
  const byId = new Map(episodeClients.map((client) => [client.id, client]));
  for (const client of directoryOnlyClients) {
    if (!byId.has(client.id)) {
      byId.set(client.id, client);
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Client ids whose episode rows match the caseload provider filter.
 * Used by the Contacts tab (rows are per contact, scoped via the subject client).
 */
export function subjectClientIdsMatchingProviderFilter(
  clients: Client[],
  clientGroups: ClientGroup[],
  providerFilter: string[],
  viewerPractitionerId?: string,
): Set<string> {
  const ids = new Set<string>();
  if (providerFilter.length === 0) return ids;
  for (const row of getEpisodeRows(clients, clientGroups)) {
    if (!episodeRowMatchesProviderFilter(row, providerFilter, { viewerPractitionerId })) continue;
    for (const member of row.clients ?? [row.client]) {
      ids.add(member.id);
    }
  }
  return ids;
}

export function clientHasEpisodes(client: Client): boolean {
  return (client.episodes?.length ?? 0) > 0;
}

export function formatClientEpisodeSummary(client: Client): string {
  const episodes = client.episodes ?? [];
  if (episodes.length === 0) return 'None';
  if (episodes.length === 1) return episodes[0].name;
  return `${episodes.length} episodes`;
}

const PLACEHOLDER_PROVIDER_LABEL = 'Provider';

/** Display label for a client's connected provider; empty when none is assigned. */
export function getClientProviderDisplay(client: Client): string {
  const raw = client.primaryProvider ?? client.provider ?? '';
  if (!raw || raw === PLACEHOLDER_PROVIDER_LABEL) return '';
  return raw;
}
